import React, { useState } from "react";
import {
  Thermometer,
  Settings,
  Eye,
  EyeOff,
  Palette,
  Target,
} from "lucide-react";
import type { HeatmapOptions, HeatmapData } from "../../types/route";

interface HeatmapControlsProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  options: Partial<HeatmapOptions>;
  onOptionsChange: (options: Partial<HeatmapOptions>) => void;
  heatmapData?: HeatmapData;
}

export const HeatmapControls: React.FC<HeatmapControlsProps> = ({
  enabled,
  onToggle,
  options,
  onOptionsChange,
  heatmapData,
}) => {
  const [showSettings, setShowSettings] = useState(false);

  const handleRadiusChange = (radius: number) => {
    onOptionsChange({ ...options, radius });
  };

  const handleBlurChange = (blur: number) => {
    onOptionsChange({ ...options, blur });
  };

  const handleOpacityChange = (minOpacity: number, maxOpacity: number) => {
    onOptionsChange({ ...options, minOpacity, maxOpacity });
  };

  const handleGradientPreset = (preset: string) => {
    const gradients: { [key: string]: { [key: string]: string } } = {
      heat: {
        0.0: "#313695",
        0.1: "#4575b4",
        0.2: "#74add1",
        0.3: "#abd9e9",
        0.4: "#e0f3f8",
        0.5: "#ffffbf",
        0.6: "#fee090",
        0.7: "#fdae61",
        0.8: "#f46d43",
        0.9: "#d73027",
        1.0: "#a50026",
      },
      traffic: {
        0.0: "#00ff00",
        0.3: "#ffff00",
        0.6: "#ff8000",
        1.0: "#ff0000",
      },
      density: {
        0.0: "#0000ff",
        0.3: "#00ffff",
        0.5: "#00ff00",
        0.7: "#ffff00",
        1.0: "#ff0000",
      },
      cool: {
        0.0: "#000080",
        0.2: "#0000ff",
        0.4: "#00ffff",
        0.6: "#40ff40",
        0.8: "#ffff00",
        1.0: "#ff8000",
      },
    };

    onOptionsChange({ ...options, gradient: gradients[preset] });
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      {/* Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Thermometer className="w-4 h-4 text-red-500" />
            <span className="font-medium text-sm text-gray-900">
              Route Density Heatmap
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              title="Heatmap Settings"
            >
              <Settings className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={() => onToggle(!enabled)}
              className={`p-1 rounded transition-colors ${
                enabled
                  ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
              title={enabled ? "Hide Heatmap" : "Show Heatmap"}
            >
              {enabled ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Stats */}
        {enabled && heatmapData && (
          <div className="mt-2 text-xs text-gray-600">
            <div className="flex justify-between">
              <span>
                Data Points: {heatmapData.totalPoints.toLocaleString()}
              </span>
              <span>Max Intensity: {heatmapData.maxIntensity}</span>
            </div>
          </div>
        )}
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="p-3 space-y-4 border-b border-gray-200">
          {/* Radius Control */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Heat Radius: {options.radius || 20}px
            </label>
            <input
              type="range"
              min="10"
              max="50"
              value={options.radius || 20}
              onChange={(e) => handleRadiusChange(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Blur Control */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Blur: {options.blur || 15}px
            </label>
            <input
              type="range"
              min="5"
              max="30"
              value={options.blur || 15}
              onChange={(e) => handleBlurChange(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Opacity Controls */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Opacity: {Math.round((options.minOpacity || 0.3) * 100)}% -{" "}
              {Math.round((options.maxOpacity || 0.8) * 100)}%
            </label>
            <div className="flex space-x-2">
              <input
                type="range"
                min="0.1"
                max="0.9"
                step="0.1"
                value={options.minOpacity || 0.3}
                onChange={(e) =>
                  handleOpacityChange(
                    Number(e.target.value),
                    options.maxOpacity || 0.8
                  )
                }
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <input
                type="range"
                min="0.2"
                max="1.0"
                step="0.1"
                value={options.maxOpacity || 0.8}
                onChange={(e) =>
                  handleOpacityChange(
                    options.minOpacity || 0.3,
                    Number(e.target.value)
                  )
                }
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>

          {/* Color Presets */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              <Palette className="w-3 h-3 inline mr-1" />
              Color Scheme
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { name: "Heat", key: "heat", colors: ["#313695", "#a50026"] },
                {
                  name: "Traffic",
                  key: "traffic",
                  colors: ["#00ff00", "#ff0000"],
                },
                {
                  name: "Density",
                  key: "density",
                  colors: ["#0000ff", "#ff0000"],
                },
                { name: "Cool", key: "cool", colors: ["#000080", "#ff8000"] },
              ].map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => handleGradientPreset(preset.key)}
                  className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 transition-colors"
                  style={{
                    background: `linear-gradient(to right, ${preset.colors[0]}, ${preset.colors[1]})`,
                  }}
                >
                  <span className="text-white font-medium drop-shadow">
                    {preset.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      {enabled && (
        <div className="p-3">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>Low Density</span>
            <span>High Density</span>
          </div>
          <div
            className="h-3 rounded"
            style={{
              background: `linear-gradient(to right, ${Object.values(
                options.gradient || {
                  0.0: "#313695",
                  0.5: "#ffffbf",
                  1.0: "#a50026",
                }
              ).join(", ")})`,
            }}
          />
          <div className="flex items-center justify-center mt-2 text-xs text-gray-500">
            <Target className="w-3 h-3 mr-1" />
            Shows where bikes have traveled most frequently
          </div>
        </div>
      )}
    </div>
  );
};
